import { computed } from 'vue'
import type { TicketsState } from './state'
import type { Ticket, TicketType, TicketStatus, Priority } from '@/types'
import { getSLAStatus } from '@/config/sla'

export const createGetters = (state: TicketsState) => {
  const filteredTickets = computed(() => {
    let result = [...state.tickets]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (ticket) =>
          ticket.title.toLowerCase().includes(search) ||
          ticket.description.toLowerCase().includes(search)
      )
    }

    // Filter by organization
    if (state.filters.organizationId) {
      result = result.filter((ticket) => ticket.organizationId === state.filters.organizationId)
    }

    // Filter by project
    if (state.filters.projectId) {
      result = result.filter((ticket) => ticket.projectId === state.filters.projectId)
    }

    // Filter by type
    if (state.filters.type) {
      result = result.filter((ticket) => ticket.type === state.filters.type)
    }

    // Filter by status
    if (state.filters.status) {
      result = result.filter((ticket) => ticket.status === state.filters.status)
    }

    // Filter by priority
    if (state.filters.priority) {
      result = result.filter((ticket) => ticket.priority === state.filters.priority)
    }

    // Filter by assigned operator
    if (state.filters.assignedTo) {
      result = result.filter((ticket) => ticket.operatorIds.includes(state.filters.assignedTo))
    }

    return result
  })

  const ticketsByStatus = computed(() => {
    const grouped: Record<TicketStatus, Ticket[]> = {
      open: [],
      in_progress: [],
      waiting_client: [],
      resolved: [],
      closed: [],
    }

    state.tickets.forEach((ticket) => {
      if (grouped[ticket.status]) {
        grouped[ticket.status].push(ticket)
      }
    })

    return grouped
  })

  const ticketsByType = computed(() => {
    const grouped: Record<TicketType, Ticket[]> = {
      bug: [],
      support: [],
      inquiry: [],
      enhancement: [],
      quotation: [],
    }

    state.tickets.forEach((ticket) => {
      if (grouped[ticket.type]) {
        grouped[ticket.type].push(ticket)
      }
    })

    return grouped
  })

  const ticketsByPriority = computed(() => {
    const grouped: Record<Priority, Ticket[]> = {
      critical: [],
      high: [],
      medium: [],
      low: [],
    }

    state.tickets.forEach((ticket) => {
      if (grouped[ticket.priority]) {
        grouped[ticket.priority].push(ticket)
      }
    })

    return grouped
  })

  const ticketsByOrganization = computed(() => {
    const grouped: Record<string, Ticket[]> = {}

    state.tickets.forEach((ticket) => {
      if (!grouped[ticket.organizationId]) {
        grouped[ticket.organizationId] = []
      }
      grouped[ticket.organizationId].push(ticket)
    })

    return grouped
  })

  const openTickets = computed(() => {
    return state.tickets.filter(
      (ticket) => ticket.status === 'open' || ticket.status === 'in_progress'
    )
  })

  const overdueTickets = computed(() => {
    return state.tickets.filter((ticket) => {
      if (ticket.status === 'resolved' || ticket.status === 'closed') {
        return false
      }

      // Check response SLA
      if (!ticket.respondedAt) {
        const responseStatus = getSLAStatus(ticket.slaResponseDue)
        if (responseStatus === 'overdue') return true
      }

      // Check resolution SLA
      if (!ticket.resolvedAt) {
        const resolutionStatus = getSLAStatus(ticket.slaResolutionDue)
        if (resolutionStatus === 'overdue') return true
      }

      return false
    })
  })

  const atRiskTickets = computed(() => {
    return state.tickets.filter((ticket) => {
      if (ticket.status === 'resolved' || ticket.status === 'closed') {
        return false
      }

      // Check response SLA
      if (!ticket.respondedAt) {
        const responseStatus = getSLAStatus(ticket.slaResponseDue)
        if (responseStatus === 'at_risk') return true
      }

      // Check resolution SLA
      if (!ticket.resolvedAt) {
        const resolutionStatus = getSLAStatus(ticket.slaResolutionDue)
        if (resolutionStatus === 'at_risk') return true
      }

      return false
    })
  })

  const stats = computed(() => {
    const total = state.tickets.length
    const open = state.tickets.filter(
      (t) => t.status === 'open' || t.status === 'in_progress' || t.status === 'waiting_client'
    ).length
    const resolved = state.tickets.filter((t) => t.status === 'resolved').length
    const closed = state.tickets.filter((t) => t.status === 'closed').length
    const overdue = overdueTickets.value.length
    const atRisk = atRiskTickets.value.length
    const critical = state.tickets.filter(
      (t) => t.priority === 'critical' && t.status !== 'closed' && t.status !== 'resolved'
    ).length
    const bugs = state.tickets.filter(
      (t) => t.type === 'bug' && t.status !== 'closed' && t.status !== 'resolved'
    ).length

    // SLA compliance
    const resolvedWithSLA = state.tickets.filter(
      (t) => t.slaResolutionMet !== undefined
    )
    const slaCompliance = resolvedWithSLA.length > 0
      ? Math.round(
          (resolvedWithSLA.filter((t) => t.slaResolutionMet).length / resolvedWithSLA.length) * 100
        )
      : 100

    return {
      total,
      open,
      resolved,
      closed,
      overdue,
      atRisk,
      critical,
      bugs,
      slaCompliance,
    }
  })

  const getTicketById = (id: string): Ticket | undefined => {
    return state.tickets.find((ticket) => ticket.id === id)
  }

  const getTicketsByProjectId = (projectId: string): Ticket[] => {
    return state.tickets.filter((ticket) => ticket.projectId === projectId)
  }

  const getTicketsByOrganizationId = (organizationId: string): Ticket[] => {
    return state.tickets.filter((ticket) => ticket.organizationId === organizationId)
  }

  const getTicketSLAInfo = (ticket: Ticket) => {
    const responseStatus = ticket.respondedAt
      ? ticket.slaResponseMet
        ? 'met'
        : 'breached'
      : getSLAStatus(ticket.slaResponseDue)

    const resolutionStatus = ticket.resolvedAt
      ? ticket.slaResolutionMet
        ? 'met'
        : 'breached'
      : getSLAStatus(ticket.slaResolutionDue)

    return {
      response: {
        status: responseStatus,
        dueDate: ticket.slaResponseDue,
        completedAt: ticket.respondedAt,
        met: ticket.slaResponseMet,
      },
      resolution: {
        status: resolutionStatus,
        dueDate: ticket.slaResolutionDue,
        completedAt: ticket.resolvedAt,
        met: ticket.slaResolutionMet,
      },
    }
  }

  return {
    filteredTickets,
    ticketsByStatus,
    ticketsByType,
    ticketsByPriority,
    ticketsByOrganization,
    openTickets,
    overdueTickets,
    atRiskTickets,
    stats,
    getTicketById,
    getTicketsByProjectId,
    getTicketsByOrganizationId,
    getTicketSLAInfo,
  }
}
